import { Injectable, computed, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { SavedCard } from './payment-method-state.service';

export interface SelectedPaymentAmount {
  /** Label of the option the user picked, e.g. 'Amount Due'. */
  paymentAmountType: string;
  /** Formatted amount to charge, e.g. '$812.98'. */
  selectedAmtToPay: string;
}

/**
 * Selection state for the make-payment flow.
 *
 * The payment cards are rendered through CardOutletComponent, which only binds
 * inputs — this service is the channel the cards use to report the user's
 * choices back up to MakePaymentComponent.
 */
@Injectable({ providedIn: 'root' })
export class MakePaymentStateService {
  private readonly _selectedAmount = signal<SelectedPaymentAmount | null>(null);
  private readonly _selectedCard = signal<SavedCard | null>(null);
  private readonly _chargeResponse = signal<any>(null);

  /** Emits when make-payment wants payment-method to trigger OneInc save. */
  private readonly _triggerSave$ = new Subject<void>();
  readonly triggerSave$ = this._triggerSave$.asObservable();

  /** Emits the tokenId after OneInc save succeeds in payment-method. */
  private readonly _saveComplete$ = new Subject<string>();
  readonly saveComplete$ = this._saveComplete$.asObservable();

  readonly selectedAmount = this._selectedAmount.asReadonly();
  readonly selectedCard = this._selectedCard.asReadonly();
  readonly chargeResponse = this._chargeResponse.asReadonly();

  /** True once a valid, non-zero amount has been selected. */
  readonly hasAmount = computed(() => {
    const amount = this._selectedAmount();
    return !!amount && parseAmount(amount.selectedAmtToPay) > 0;
  });

  /** True once a usable payment method is selected. */
  readonly hasPaymentMethod = computed(() => !!this._selectedCard());

  /** Both an amount and a payment method are chosen — payment can be submitted. */
  readonly canSubmit = computed(() => this.hasAmount() && this.hasPaymentMethod());

  setAmount(amount: SelectedPaymentAmount | null): void {
    this._selectedAmount.set(amount);
  }

  setPaymentMethod(card: SavedCard | null): void {
    this._selectedCard.set(card);
  }

  /** Store the charge response so payment-summary can read it after navigation. */
  setChargeResponse(response: any): void {
    this._chargeResponse.set(response);
  }

  /** Called by make-payment to request OneInc save from payment-method. */
  triggerSavePayment(): void {
    this._triggerSave$.next();
  }

  /** Called by payment-method after OneInc save succeeds with the new tokenId. */
  notifySaveComplete(tokenId: string): void {
    this._saveComplete$.next(tokenId);
  }

  reset(): void {
    this._selectedAmount.set(null);
    this._selectedCard.set(null);
    // Don't reset chargeResponse — payment-summary needs it after navigation
  }
}

function parseAmount(value: string | number | undefined): number {
  if (typeof value === 'number') {
    return isNaN(value) ? 0 : value;
  }
  return parseFloat(String(value ?? '').replace(/[^0-9.]/g, '')) || 0;
}
